import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  Toolbar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  FileDownload as ExportIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Remove as RemoveIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { api } from '../../../services/authService';

const LeadListDetail = () => {
  const navigate = useNavigate();
  const { listId } = useParams();
  const currentUser = useSelector((state) => state.auth.user);
  
  // State management
  const [list, setList] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterStep, setFilterStep] = useState('');
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [funnelSteps, setFunnelSteps] = useState([]);
  
  // Menu states
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  
  // Dialog states
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalLeads, setTotalLeads] = useState(0);

  useEffect(() => {
    fetchListData();
  }, [listId]);

  useEffect(() => {
    fetchLeads();
  }, [listId, page, rowsPerPage, searchTerm, filterStatus, filterStep]);


  const fetchListData = async () => {
    setLoading(true);
    try {
      // First fetch the list data
      const listResponse = await api.get(`/crm/lists/${listId}/`);
      setList(listResponse.data);
      
      // Initialize arrays to prevent undefined errors
      setLeadStatuses([]);
      setFunnelSteps([]);
      
      // Then fetch campaign-dependent data if campaign exists
      if (listResponse.data.campaign) {
        try {
          const [statusesResponse, stepsResponse] = await Promise.all([
            api.get(`/crm/lead-statuses/`),
            api.get(`/crm/steps/?campaign_id=${listResponse.data.campaign}`)
          ]);
          
          setLeadStatuses(statusesResponse.data || []);
          setFunnelSteps(stepsResponse.data || []);
        } catch (apiError) {
          console.error('Error fetching campaign-dependent data:', apiError);
          // Keep empty arrays if API calls fail
        }
      } else {
        // If no campaign, fetch all statuses and steps (or set empty arrays)
        try {
          const [statusesResponse, stepsResponse] = await Promise.all([
            api.get('/crm/lead-statuses/'),
            api.get('/crm/steps/')
          ]);
          
          setLeadStatuses(statusesResponse.data || []);
          setFunnelSteps(stepsResponse.data || []);
        } catch (apiError) {
          console.error('Error fetching all statuses and steps:', apiError);
          // Keep empty arrays if API calls fail
        }
      }
    } catch (error) {
      console.error('Error fetching list data:', error);
      // Initialize arrays to prevent undefined errors
      setLeadStatuses([]);
      setFunnelSteps([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeads = async () => {
    setLeadsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page + 1,
        page_size: rowsPerPage,
        ...(searchTerm && { search: searchTerm }),
        ...(filterStatus && { status: filterStatus }),
        ...(filterStep && { step: filterStep })
      });
      
      const response = await api.get(`/crm/lists/${listId}/leads/?${params.toString()}`);
      
      // Handle different possible response structures
      let leadsData = [];
      let totalCount = 0;
      
      if (Array.isArray(response.data)) {
        // If response.data is directly an array
        leadsData = response.data;
        totalCount = response.data.length;
      } else if (response.data && Array.isArray(response.data.results)) {
        // If response.data has a results array (pagination)
        leadsData = response.data.results;
        totalCount = response.data.count || response.data.results.length;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        // If response.data has a nested data array
        leadsData = response.data.data;
        totalCount = response.data.count || response.data.data.length;
      } else {
        // Fallback - try to find any array in the response
        Object.keys(response.data || {}).forEach(key => {
          if (Array.isArray(response.data[key])) {
            if (key.toLowerCase().includes('lead') || key.toLowerCase().includes('result')) {
              leadsData = response.data[key];
              totalCount = response.data.count || response.data[key].length;
            }
          }
        });
      }
      
      setLeads(leadsData);
      setTotalLeads(totalCount);
    } catch (error) {
      console.error('Error fetching leads:', error);
      // Set empty arrays on error to prevent undefined issues
      setLeads([]);
      setTotalLeads(0);
    } finally {
      setLeadsLoading(false);
    }
  };

  const handleMenuClick = (event, lead) => {
    setAnchorEl(event.currentTarget);
    setSelectedLead(lead);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedLead(null);
  };

  const handleViewLead = () => {
    handleMenuClose();
    navigate(`/crm/leads/${selectedLead.id}/details`);
  };

  const handleRemoveLead = () => {
    handleMenuClose();
    setRemoveDialogOpen(true);
  };

  const confirmRemoveLead = async () => {
    try {
      await api.post(`/crm/lists/${listId}/remove_leads/`, {
        lead_ids: [selectedLead.id]
      });
      setRemoveDialogOpen(false);
      fetchLeads();
    } catch (error) {
      console.error('Error removing lead:', error);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedLeads((leads || []).map(lead => lead.id));
    } else {
      setSelectedLeads([]);
    }
  };

  const handleSelectLead = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleBulkRemove = async () => {
    if (window.confirm(`Are you sure you want to remove ${selectedLeads.length} leads from this list?`)) {
      try {
        await api.post(`/crm/lists/${listId}/remove_leads/`, {
          lead_ids: selectedLeads
        });
        setSelectedLeads([]);
        fetchLeads();
      } catch (error) {
        console.error('Error removing leads:', error);
      }
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({
        format: exportFormat,
        ...(searchTerm && { search: searchTerm }),
        ...(filterStatus && { status: filterStatus }),
        ...(filterStep && { step: filterStep })
      });
      
      const response = await api.get(`/crm/lists/${listId}/export/?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${list.name}_leads.${exportFormat}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setExportDialogOpen(false);
    } catch (error) {
      console.error('Error exporting leads:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'draft': return 'warning';
      case 'archived': return 'default';
      default: return 'default';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'manual': return 'Manual';
      case 'dynamic': return 'Dynamic';
      case 'filtered': return 'Filtered';
      default: return type;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!list) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">List not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link color="inherit" href="/crm/dashboard">
            Dashboard
          </Link>
          <Link color="inherit" href="/crm/lists">
            Lead Lists
          </Link>
          <Typography color="text.primary">{list.name}</Typography>
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1" color="text.primary">
            {list.name}
          </Typography>
          <Box>
            <Button
              startIcon={<AddIcon />}
              onClick={() => navigate(`/crm/lists/${listId}/add-leads`)}
              variant="outlined"
              sx={{ mr: 1 }}
            >
              Add Leads
            </Button>
            <Button
              startIcon={<ExportIcon />}
              onClick={() => setExportDialogOpen(true)}
              variant="outlined"
              sx={{ mr: 1 }}
            >
              Export
            </Button>
            <Button
              startIcon={<EditIcon />}
              onClick={() => navigate(`/crm/lists/${listId}/edit`)}
              variant="contained"
            >
              Edit
            </Button>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* List Information */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                List Details
              </Typography>
              
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Type"
                    secondary={
                      <Chip label={getTypeLabel(list.list_type)} size="small" />
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Status"
                    secondary={
                      <Chip 
                        label={list.status} 
                        color={getStatusColor(list.status)}
                        size="small"
                      />
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Created"
                    secondary={new Date(list.created_at).toLocaleDateString()}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Last Updated"
                    secondary={new Date(list.updated_at).toLocaleDateString()}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Total Leads"
                    secondary={list.total_leads || 0}
                  />
                </ListItem>
                {list.description && (
                  <ListItem>
                    <ListItemText
                      primary="Description"
                      secondary={list.description}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Leads Table */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Leads in List
              </Typography>
              
              {/* Search and Filters */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    placeholder="Search leads..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="">All Statuses</MenuItem>
                      {(leadStatuses || []).map(status => (
                        <MenuItem key={status.id} value={status.id}>
                          {status.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Step</InputLabel>
                    <Select
                      value={filterStep}
                      onChange={(e) => setFilterStep(e.target.value)}
                      label="Step"
                    >
                      <MenuItem value="">All Steps</MenuItem>
                      {(funnelSteps || []).map(step => (
                        <MenuItem key={step.id} value={step.id}>
                          {step.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Tooltip title="Refresh">
                    <IconButton onClick={fetchLeads} disabled={leadsLoading}>
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>

              {/* Bulk Actions Toolbar */}
              {selectedLeads.length > 0 && (
                <Box sx={{ mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ display: 'inline', mr: 2 }}>
                    Selected: {selectedLeads.length} leads
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<RemoveIcon />}
                    onClick={handleBulkRemove}
                    color="error"
                    sx={{ mr: 1 }}
                  >
                    Remove from List
                  </Button>
                </Box>
              )}

              {/* Leads Table */}
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={(leads || []).length > 0 && selectedLeads.length === (leads || []).length}
                          indeterminate={selectedLeads.length > 0 && selectedLeads.length < (leads || []).length}
                          onChange={handleSelectAll}
                        />
                      </TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Step</TableCell>
                      <TableCell>Added</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leadsLoading ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <CircularProgress />
                        </TableCell>
                      </TableRow>
                    ) : (leads || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No leads found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      (leads || []).map((lead) => (
                        <TableRow key={lead.id} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedLeads.includes(lead.id)}
                              onChange={() => handleSelectLead(lead.id)}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="subtitle2" sx={{ cursor: 'pointer' }} onClick={() => navigate(`/crm/leads/${lead.id}/details`)}>
                              {lead.first_name} {lead.last_name}
                            </Typography>
                          </TableCell>
                          <TableCell>{lead.email}</TableCell>
                          <TableCell>{lead.phone}</TableCell>
                          <TableCell>
                            <Chip 
                              label={lead.status?.name || 'No Status'} 
                              size="small"
                              color={lead.status?.name === 'Active' ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={lead.current_step?.name || 'No Step'} 
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {lead.added_to_list_at ? 
                              new Date(lead.added_to_list_at).toLocaleDateString() : 
                              'N/A'
                            }
                          </TableCell>
                          <TableCell align="right">
                            <IconButton onClick={(e) => handleMenuClick(e, lead)}>
                              <MoreVertIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {totalLeads === 0 ? (
                    'No leads found'
                  ) : (
                    `Showing ${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, totalLeads)} of ${totalLeads} leads`
                  )}
                </Typography>
                <Box>
                  <Button
                    disabled={page === 0}
                    onClick={() => setPage(page - 1)}
                    sx={{ mr: 1 }}
                  >
                    Previous
                  </Button>
                  <Button
                    disabled={(page + 1) * rowsPerPage >= totalLeads}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewLead}>
          <ViewIcon sx={{ mr: 1 }} />
          View Lead
        </MenuItem>
        <MenuItem onClick={handleRemoveLead} sx={{ color: 'error.main' }}>
          <RemoveIcon sx={{ mr: 1 }} />
          Remove from List
        </MenuItem>
      </Menu>

      {/* Remove Lead Confirmation Dialog */}
      <Dialog open={removeDialogOpen} onClose={() => setRemoveDialogOpen(false)}>
        <DialogTitle>Remove Lead</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove "{selectedLead?.first_name} {selectedLead?.last_name}" from this list?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmRemoveLead} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)}>
        <DialogTitle>Export Leads</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Export Format</InputLabel>
            <Select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value)}
              label="Export Format"
            >
              <MenuItem value="csv">CSV</MenuItem>
              <MenuItem value="xlsx">Excel (XLSX)</MenuItem>
              <MenuItem value="json">JSON</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleExport} variant="contained">
            Export
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadListDetail; 